/**
 * CommercialActivationKPIs — minimal, embeddable summary card.
 *
 * Renders the /api/admin/commercial-activation/summary KPIs as a compact
 * 4-row strip. Used inside existing admin tabs (QuoteRequestsTab,
 * ProvidersTab) so admins see the activation backlog without us
 * shipping a whole new dashboard tab yet.
 *
 * No write actions — purely read-only.
 */
import { useEffect, useState } from 'react';
import { Activity, AlertTriangle, BadgeCheck, Link as LinkIcon } from 'lucide-react';

export default function CommercialActivationKPIs({ token }) {
  const [kpis, setKpis] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/admin/commercial-activation/summary', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = await res.json();
        if (!cancelled) setKpis(d.kpis);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (error) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="ca-kpis-error">
        Activation KPIs niet geladen: {error}
      </div>
    );
  }
  if (!kpis) return null;

  const rows = [
    {
      icon: LinkIcon,
      label: 'Affiliate',
      pieces: [
        { k: 'ready', v: kpis.affiliate_ready },
        { k: 'needs link', v: kpis.needs_affiliate_link },
        { k: 'quote-only', v: kpis.quote_only },
      ],
    },
    {
      icon: BadgeCheck,
      label: 'Providers',
      pieces: [
        { k: 'total', v: kpis.providers_total },
        { k: 'approved', v: kpis.providers_approved },
        { k: 'visible', v: kpis.providers_visible },
      ],
    },
    {
      icon: Activity,
      label: 'Outreach',
      pieces: [
        { k: 'ready-for-review', v: kpis.providers_ready_for_review },
        { k: 'declined', v: kpis.providers_declined },
        { k: 'no-response', v: kpis.providers_no_response },
      ],
    },
    {
      icon: AlertTriangle,
      label: 'Gaps',
      pieces: [
        { k: 'cat × region cells <3', v: kpis.category_region_gap_cells },
        { k: 'cats met 0 providers', v: (kpis.categories_with_zero_providers || []).length },
      ],
    },
  ];

  return (
    <div
      className="rounded-xl border border-primary/20 bg-white p-3 sm:p-4 mb-4"
      data-testid="commercial-activation-kpis"
    >
      <div className="text-[11px] uppercase tracking-wider font-semibold text-primary mb-2">
        Commercial Activation — KPIs
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {rows.map(({ icon: Icon, label, pieces }) => (
          <div
            key={label}
            className="flex items-start gap-2 bg-muted/30 border border-border rounded-lg p-2"
            data-testid={`ca-kpi-${label.toLowerCase()}`}
          >
            <Icon className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <div className="text-sm leading-snug">
              <div className="font-semibold text-foreground">{label}</div>
              <div className="text-[12px] text-muted-foreground">
                {pieces.map((p, i) => (
                  <span key={p.k}>
                    {i > 0 && ' · '}
                    <span className="font-medium text-foreground">{p.v}</span> {p.k}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {(kpis.categories_with_zero_providers || []).length > 0 && (
        <div
          className="mt-2 text-[12px] text-muted-foreground"
          data-testid="ca-kpis-zero-cats"
        >
          Geen providers in: <span className="text-rose-700 font-medium">{kpis.categories_with_zero_providers.join(', ')}</span>
        </div>
      )}
    </div>
  );
}
